import React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ModalWrapper from "components/ModalWrapper";
import Input from "components/Input";
import { withTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { saveFormData } from "store/slices/movieboxSlice";
import CountryModal from "components/CountryModal";
import LanguageModal from "components/LanguageModal";
import ClassificationModal from "components/ClassificationModal";
import YearModal from "components/YearModal";
import CategoryModal from "components/CategoryModal";

// Validation schema using Yup
const validationSchema = yup.object().shape({
  title: yup.string().required("Series title is required"),
  description: yup.string().required("Description is required"),
  classifications: yup.array().of(yup.object().shape({
    display_name: yup.string().required("Classification is required"),
    id: yup.number().required("Classification ID is required")
  })).required("At least one classification is required"),
  language: yup.string().required("Language is required"),
  country: yup.string().required("Country is required"),
  releaseYear: yup.string()
    .required("Release year is required")
    .matches(/^\d{4}$/, "Must be a valid year (4 digits)")
    .test(
      "is-valid-year",
      "Year must be between 1900 and current year",
      (value) => {
        const currentYear = new Date().getFullYear();
        return value && parseInt(value) >= 1900 && parseInt(value) <= currentYear;
      }
    )
});

function AddTvSeriesModal({ isOpen, onClose, onSubmit, t }) {
  const dispatch = useDispatch();
  const methods = useForm({
    defaultValues: {
      title: "",
      description: "",
      category: "",
      language: "",
      country: "",
      releaseYear: ""
    },
    resolver: yupResolver(validationSchema),
    mode: "onSubmit"
  });

  const { formState: { errors }, trigger } = methods;
  const navigate = useNavigate();

  // Custom onChange handlers for select fields
  // const handleCategoryChange = (option) => {
  //   setValue('category', option.value, { shouldValidate: true });
  //   trigger('category'); // Trigger validation after setting value
  // };

  // const handleLanguageChange = (option) => {
  //   setValue('language', option.value, { shouldValidate: true });
  //   trigger('language'); // Trigger validation after setting value
  // };

  // const handleCountryChange = (option) => {
  //   setValue('country', option.value, { shouldValidate: true });
  //   trigger('country'); // Trigger validation after setting value
  // };

  const handleSubmit = async (data) => {
    // Trigger validation for all fields before submission
    const allFieldsValid = await trigger([
      'title',
      'description',
      'category',
      'language',
      'country',
      'releaseYear'
    ]);

    if (allFieldsValid) {
      // Save form data to Redux only on successful submission
      dispatch(saveFormData(data));

      // Call the original onSubmit if provided
      if (onSubmit) {
        onSubmit(data);
      }

      onClose();
      navigate(`/movie-box/add-season`);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalWrapper title="Add TV Series" onClose={onClose}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)} className="space-y-4">
          <Input
            name="title"
            label="Series Title"
            placeholder="Enter series title"
            error={errors.title?.message}
          />

          <Input
            name="description"
            label="Series description"
            placeholder="Enter description"
            type="textarea"
            rows={3}
            error={errors.description?.message}
          />

          <div className="grid grid-cols-2 gap-4">
            <CategoryModal label="Category" name="category" error={errors.category?.message} />

            <LanguageModal label="Language" name="language" error={errors.language?.message} />

          </div>

          <div className="grid grid-cols-2 gap-4">
            <CountryModal label="Country" name={'country'} error={errors.country?.message} />

            <YearModal name={'releaseYear'} label="Release Year" error={errors.releaseYear?.message} />

          </div>
            <ClassificationModal label="Classifications" name="classifications" error={errors.classifications?.message} />

          <div className="flex  space-x-3 mt-6">
       
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 flex-1 text-white rounded-md hover:bg-blue-700"
            >
              Continue
            </button>
          </div>
        </form>
      </FormProvider>
    </ModalWrapper>
  );
}

export default withTranslation()(AddTvSeriesModal);